"use client";

import React from "react";
import {
  Check,
  Home,
  FileText,
  MapPin,
  Video,
  CheckCircle,
  ChevronRight,
} from "lucide-react";

interface StepIndicatorProps {
  currentStep: number;
  completedSteps: {
    businessDetails: boolean;
    personalDetails: boolean;
    documentUpload: boolean;
    addressVerification: boolean;
    videoVerification: boolean;
  };
  rejectedSteps?: {
    businessDetails: boolean;
    personalDetails: boolean;
    documentUpload: boolean;
    addressVerification: boolean;
    videoVerification: boolean;
  };
}

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  completedSteps,
  rejectedSteps,
}) => {
  const steps: Step[] = [
    {
      id: 1,
      title: "Business Details",
      description: "",
      icon: <Home className="w-6 h-6" />,
    },
    {
      id: 2,
      title: "Personal Information",
      description: "",
      icon: <FileText className="w-6 h-6" />,
    },
    {
      id: 3,
      title: "Document Upload",
      description: "",
      icon: <FileText className="w-6 h-6" />,
    },
    {
      id: 4,
      title: "Address Verification",
      description: "",
      icon: <MapPin className="w-6 h-6" />,
    },
    {
      id: 5,
      title: "Video Verification",
      description: "",
      icon: <Video className="w-6 h-6" />,
    },
    {
      id: 6,
      title: "Approval or Rejection",
      description: "",
      icon: <CheckCircle className="w-6 h-6" />,
    },
  ];

  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) {
      return "completed";
    } else if (stepId === currentStep) {
      return "active";
    } else {
      return "pending";
    }
  };

  const isStepCompleted = (stepId: number) => {
    switch (stepId) {
      case 1:
        return completedSteps.businessDetails;
      case 2:
        return completedSteps.personalDetails;
      case 3:
        return completedSteps.documentUpload;
      case 4:
        return completedSteps.addressVerification;
      case 5:
        return completedSteps.videoVerification;
      case 6:
        return completedSteps.videoVerification; // Status step is "completed" when video is done
      default:
        return false;
    }
  };

  const isStepRejected = (stepId: number) => {
    if (!rejectedSteps) return false;
    switch (stepId) {
      case 1:
        return rejectedSteps.businessDetails;
      case 2:
        return rejectedSteps.personalDetails;
      case 3:
        return rejectedSteps.documentUpload;
      case 4:
        return rejectedSteps.addressVerification;
      case 5:
        return rejectedSteps.videoVerification;
      case 6:
        return false; // Status step itself is never rejected
      default:
        return false;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Desktop Step Indicator */}
      <div className="hidden md:flex items-center justify-center py-4 px-4">
        <div className="flex items-center justify-between w-full max-w-5xl py-2">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id);
            const isCompleted = isStepCompleted(step.id);
            const isRejected = isStepRejected(step.id);
            const isActive = status === "active";

            return (
              <React.Fragment key={step.id}>
                {/* Step */}
                <div className="flex flex-col items-center flex-1">
                  {/* Step Circle */}
                  <div
                    className={`
                    relative w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all duration-300 transform
                    ${
                      isRejected
                        ? "bg-red-500 shadow-lg shadow-red-500/30 border-2 border-red-400"
                        : isCompleted
                          ? "bg-primary shadow-lg shadow-primary/30 border-2 border-primary/50 scale-105"
                          : isActive
                            ? "bg-primary shadow-xl shadow-primary/40 border-2 border-primary animate-pulse scale-110"
                            : "bg-muted border-2 border-muted-foreground/20 hover:border-primary/30"
                    }
                  `}
                  >
                    {isRejected ? (
                      <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                        <span className="text-red-500 text-xs font-bold">
                          !
                        </span>
                      </div>
                    ) : isCompleted ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <div
                        className={
                          isActive ? "text-white" : "text-auth-text-muted"
                        }
                      >
                        {step.icon}
                      </div>
                    )}
                  </div>

                  {/* Step Text */}
                  <div className="text-center max-w-[140px]">
                    <p
                      className={`text-xs lg:text-sm font-semibold transition-colors duration-300 ${
                        isRejected
                          ? "text-red-500"
                          : isCompleted
                            ? "text-primary"
                            : isActive
                              ? "text-primary font-bold"
                              : "text-muted-foreground"
                      } leading-tight`}
                    >
                      {step.title}
                    </p>
                  </div>
                </div>

                {/* Arrow Connector */}
                {index < steps.length - 1 && (
                  <div className="flex items-center justify-center px-4">
                    <ChevronRight
                      className={`w-5 h-5 transition-colors duration-300 ${
                        step.id < currentStep
                          ? "text-primary/80"
                          : step.id === currentStep
                            ? "text-primary animate-pulse"
                            : "text-muted-foreground/50"
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Mobile Step Indicator */}
      <div className="md:hidden mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            Step {currentStep} of {steps.length}
          </h2>
          <div className="text-sm text-auth-text-muted">
            {Math.round((currentStep / steps.length) * 100)}% Complete
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-muted rounded-full h-3 mb-4 shadow-inner">
          <div
            className="bg-gradient-to-r from-primary to-primary/80 h-3 rounded-full transition-all duration-500 shadow-sm"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>

        {/* Current Step Info */}
        <div className="flex items-center space-x-3 p-4 bg-card border border-border rounded-2xl shadow-sm">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-lg">
            <div className="text-primary-foreground">
              {steps[currentStep - 1]?.icon}
            </div>
          </div>
          <div>
            <p className="text-foreground font-medium">
              {steps[currentStep - 1]?.title}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;
